import { getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  getAuth,
  initializeAuth,
  type Auth,
} from "firebase/auth";

const firebaseConfig: FirebaseOptions = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
};

const hasFirebaseConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.appId && firebaseConfig.projectId);
let firebaseAuthInstance: Auth | null = null;
let firebaseAppInstance: FirebaseApp | null = null;

export function getFirebaseApp() {
  if (!hasFirebaseConfig) {
    return null;
  }

  if (!firebaseAppInstance) {
    firebaseAppInstance = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }

  return firebaseAppInstance;
}

export function getFirebaseAuth() {
  const app = getFirebaseApp();

  if (!app) {
    return null;
  }

  if (!firebaseAuthInstance) {
    try {
      firebaseAuthInstance = initializeAuth(app, {
        persistence: browserLocalPersistence,
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch {
      firebaseAuthInstance = getAuth(app);
    }
  }

  return firebaseAuthInstance;
}

export const firebaseAuth = new Proxy({} as Auth, {
  get(_target, property) {
    const auth = getFirebaseAuth();

    if (!auth) {
      if (property === "currentUser") {
        return null;
      }

      if (property === "signOut") {
        return async () => undefined;
      }

      return undefined;
    }

    const value = auth[property as keyof Auth];
    return typeof value === "function" ? value.bind(auth) : value;
  },
});
